import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { fromJS } from 'immutable';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import styles from './styles';
import messages from '../../messages';
import ReservationsDialog from '../../../../../../Shopping/CreateBooking/components/ReservationsDialog/ReservationsDialog';
import PreviewDialog from '../PreviewDialog/PreviewDialog';
import { validateShoppingApi } from '../../../../../apiBooking';
import ExtendedSnackbar from '../../../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import ItineraryDetails from '../../../../../../Shopping/components/Fare/ItineraryDetails';
import CarbonOffset from '../../../../../../Shopping/components/Fare/CarbonOffset';
import TwoColRow from '../../../../../../Shopping/components/Fare/TwoColRow';
import { getIntermediateTravelPointsApi } from '../../../../../../Shopping/apiShopping';
import fareItineraryMessages from '../../../../../../Shopping/components/Fare/messagesFareItinerary';

const adaptDataForReservationsDialog = (trainJourneysData) => {
	const shoppingLegs = trainJourneysData.data.ShoppingLegs.map((leg) =>
		leg.ShoppingLegSolutions.map((solution) => ({
			leg: {
				...leg,
				ShoppingLegSolutions: [solution],
			},
			fare: {
				priceId: solution.ShoppingLegFares[0].ShoppingLegFarePriceId,
				isOverbooked: solution.ShoppingLegFares[0].isOverbooked,
				price: parseFloat(
					solution.ShoppingLegFares[0].ShoppingLegFareTotalPrice,
				),
				optionalPrices: solution.ShoppingLegFares[0].OptionalPrices,
				currency:
					solution.ShoppingLegFares[0].ShoppingLegFareTotalPriceCurrency,
				linkedReturnPrices: [],
				seatPreferences: {
					reservable:
						solution.ShoppingLegFares[0].ShoppingLegFareSeatPreferenceData
							.ShoppingLegFareSeatPreferenceDataReservable,
					reservables:
						solution.ShoppingLegFares[0].ShoppingLegFareSeatPreferenceData.ShoppingLegFareSeatPreferenceDataReservables.map(
							(r) => ({
								linkedPasseger: r.LinkedPasseger,
								linkedTravelSegment: r.LinkedTravelSegment,
								reservable: r.Reservable,
							}),
						),
					preferences:
						solution.ShoppingLegFares[0].ShoppingLegFareSeatPreferenceData.ShoppingLegFareSeatPreferences.map(
							(preference) => ({
								description: preference.ShoppingLegFareSeatPreferenceDataDesc,
								name: preference.ShoppingLegFareSeatPreferenceDataName,
								type: preference.ShoppingLegFareSeatPreferenceDataType,
								marketingCarrier: preference.ShoppingLegFareMarketingCarrier,
								serviceClass: preference.ShoppingLegFareServiceClass,
								equipmentType: preference.ShoppingLegFareSupplierEquipmentType,
								travelSegmentIDRef:
									preference.ShoppingLegFareTravelSegmentIDRef,
							}),
						),
				},
				rules: solution.ShoppingLegFares[0].ShoppingLegFareRules.map(
					(item) => ({
						category: item.FareRuleCategory,
						descriptions:
							item.FareRulesDescription &&
							item.FareRulesDescription.map(
								(description) => description.Description,
							),
					}),
				),
				amenities:
					solution.ShoppingLegFares[0].ShoppingLegFareServicesAndAmenities.map(
						(item) => ({
							code: item.ShoppingLegFareServiceAndAmenityCode,
							desc: item.ShoppingLegFareServiceAndAmenityDesc,
						}),
					),
				fareCodes: solution.ShoppingLegFares[0].ShoppingLegFareCodeInfo.map(
					(item) => ({
						farePrice: Number(item.ShoppingLegFarePrice),
						fareClass: item.ShoppingLegFareFareClass,
						fareClassDesc: item.ShoppingLegFareFareClassDesc,
						cabinClass: item.ShoppingLegFareCabinClass,
						serviceClass: item.ShoppingLegFareServiceClass,
						primaryColumnClass: item.ShoppingLegFarePrimaryColumnClass,
						openReturn: item.ShoppingLegFareOpenReturn === 'YES',
						reservableCode: item.ShoppingLegFareReservableCode,
						reservableDesc: item.ShoppingLegFareReservableDesc,
						applicabilityCode: item.ShoppingLegFareApplicabilityCode,
						applicabilityDesc: item.ShoppingLegFareApplicabilityDesc.replace(
							/(.*)Schedule/i,
							'$1',
						).trim(),
						expirationDate: item.expirationDate,
						scheduleClass: item.scheduleClass,
						fareTypeClass: item.fareTypeClass,
					}),
				),
				fareRulesDetails:
					solution.ShoppingLegFares[0].ShoppingLegFareRulesDetails?.map(
						(item) => ({
							paxRefs: item.ShoppingFareRulesPaxRefs,
							fareClasses: item.ShoppingFareRulesFareClasses,
							displayName: item.ShoppingLegFareDisplayName,
							fareCodes: item.ShoppingLegFareCodes,
							fareOrigins: item.ShoppingLegFareOrigin,
							fareDestinations: item.ShoppingLegFareDestination,
							originTerminals: item.ShoppingLegFareOriginTerminals,
							destinationTerminals: item.ShoppingLegFareDestinationTerminals,
							fareMatrix: item.ShoppingFareRulesFareMatrix.map((rule) => ({
								isTextRule: rule.ShoppingFareRuleRuleIsTextRule,
								category: rule.FareRuleCategory,
								textRuleDesc:
									rule.FareRulesDescription &&
									rule.FareRulesDescription.map(
										(description) => description.Description,
									),
								priceType: rule.ShoppingFareRulePriceType || '',
								applicableTicketingOption:
									rule.ShoppingFareRuleApplicableTicketingOption || '',
								expirationDateTime:
									rule.ShoppingFareRuleExpirationDateTime || '',
								refundAllowedConfirmed:
									rule.ShoppingFareRuleRefundAllowedConfirmed || '',
								refundAllowedConfirmedPenalty:
									rule.ShoppingFareRuleRefundAllowedConfirmedPenalty || '',
								refundAllowedTicketed:
									rule.ShoppingFareRuleRefundAllowedTicketed || '',
								refundAllowedTicketedPenalty:
									rule.ShoppingFareRuleRefundAllowedTicketedPenalty || '',
								exchangeAllowedConfirmed:
									rule.ShoppingFareRuleExchangeAllowedConfirmed || '',
								exchangeAllowedConfirmedPenalty:
									rule.ShoppingFareRuleExchangeAllowedConfirmedPenalty || '',
								exchangeAllowedTicketed:
									rule.ShoppingFareRuleExchangeAllowedTicketed || '',
								exchangeAllowedTicketedPenalty:
									rule.ShoppingFareRuleExchangeAllowedTicketedPenalty || '',
								refundableIndicator:
									rule.ShoppingFareRuleRefundableIndicator || false,
							})),
						}),
					),
				discountApplied:
					solution.ShoppingLegFares[0].ShoppingLegFareDiscountApplied,
				campaignInfo: solution.ShoppingLegFares[0].ShoppingLegCampaignInfo,
			},
			legId: leg.ShoppingLegId,
			legSolutionId: solution.ShoppingLegSolutionId,
		})),
	).flat();

	const shoppingPassengers = trainJourneysData.data.ShoppingPassengers.map(
		(passenger) => ({
			...passenger,
			ShoppingPassengerRefId: passenger.ShoppingPassengerRefId,
		}),
	);

	return { shoppingLegs, shoppingPassengers };
};

const TrainJourneySelector = ({
	journeys,
	trainJourneysData,
	ticketableFareGroups,
	confirmSeatsData,
	refreshBookingParams,
	closeAllDialogs,
	isAmend,
	legSolutionId,
}) => {
	const [openSeatSelectorDialog, setOpenSeatSelectorDialog] = useState(false);
	const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
	const [selectedSeats, setSelectedSeats] = useState({});
	const [selectedJourneyData, setSelectedJourneyData] = useState();
	const [seatMapData, setSeatMapData] = useState([]);
	const [toastError, setToastError] = useState('');
	const [currentIntermediateTravelPoints, setCurrentIntermediateTravelPoints] = useState([]);
	const { shoppingLegs, shoppingPassengers } =
		adaptDataForReservationsDialog(trainJourneysData);
	const msgHideDetails = <FormattedMessage {...fareItineraryMessages.lblHideDetails} />;
	const msgViewDetails = <FormattedMessage {...fareItineraryMessages.lblViewDetails} />;
	const [showDetails, setShowDetails] = useState({});
	const [isIntermediateTravelPointsLoading, setIsIntermediateTravelPointsLoading] = useState(false);
	const handleSeatReservationsChange = (e) => {
		setSelectedSeats(e);
		setOpenPreviewDialog(true);
	};

	const handleDetails = (event, value, journey) => {
		setShowDetails({ [journey.ShoppingLegSolutionId]: value });
	

		if (value) {
			setIsIntermediateTravelPointsLoading(true);

			getIntermediateTravelPointsApi(
				journey.ShoppingContext,
				journey.ShoppingLegSolutionId	,
				() => {
					setCurrentIntermediateTravelPoints([]);
					setIsIntermediateTravelPointsLoading(false);
				},
				(response) => {
					const intermediateTravelPoints =
						response?.successResponse?.data || [];
					setCurrentIntermediateTravelPoints(intermediateTravelPoints);
					setIsIntermediateTravelPointsLoading(false);
				},
			);
		} else {
			setCurrentIntermediateTravelPoints([]);
		}
	
	};




	const getIntermediateTravelPointByTravelSegmentId = useCallback((travelSegmentId) => {
		const itpByTravelSegment = currentIntermediateTravelPoints?.find(
			(itp) => itp.TravelSegmentId === travelSegmentId,
		);
		return itpByTravelSegment?.IntermediateTravelPoints || [];
	}, [currentIntermediateTravelPoints]);


	const handleJourneySelectClick = (journeyId) => {
		const selectedJourney = shoppingLegs.find(
			(journey) => journey.legSolutionId === journeyId,
		);

		const faresSelected = fromJS([selectedJourney]);

		let priceId = null;
		if (faresSelected.size >= 1) {
			priceId = faresSelected.getIn([0, 'fare', 'priceId']);
		}

		validateShoppingApi(
			{
				dataStorageId: confirmSeatsData.DataStorageId,
				shoppingContext: confirmSeatsData.shoppingContext,
				orderLocator: confirmSeatsData.orderLocator,
				recordLocator: confirmSeatsData.recordLocator,
				ticketableFareLocators: confirmSeatsData.ticketableFareLocators,
				srtPriceId: priceId,
				srtSessionId: confirmSeatsData.srtSessionId,
				legSolutionId,
			},
			(error) => {
				setToastError(
					error?.errorResponse?.message || (
						<FormattedMessage
							{...messages.lblAmendSeatReservationsDialogError}
						/>
					),
				);
			},
			(success) => {
				setSeatMapData(success.successResponse.data.TravelSegments || []);
				setSelectedJourneyData(selectedJourney);
				setOpenSeatSelectorDialog(true);
			},
		);
	};

	return (
		<div style={styles.root}>
			<Paper elevation={1}>
				<FormattedMessage
					{...messages.lblAmendSeatReservationsDialogOutbound}
				/>
			</Paper>
			<div style={isAmend ? styles.journeysWrapAmend : styles.journeysWrap}>
				{journeys.map((journey, index) => (
					<Paper key={index} style={styles.journeyItemWrap}>
						<div style={styles.journeyHeader}>
							<div>
								<span style={styles.journeyNumber}>#{index + 1}</span>
								<span style={styles.journeyInfo}>
									<FormattedMessage
										{...messages.lblAmendSeatReservationsDialogDuration}
									/>
									: {journey.ShoppingLegSolutionDuration}{' '}
									<FormattedMessage
										{...messages.lblAmendSeatReservationsDialogChanges}
									/>
									: {journey.ShoppingLegSolutionNumChanges}
								</span>
							</div>

							<FormControlLabel
								style={{ ...styles.toggleRoot, ...styles.toggleLabel }}
								onChange={(event, val) => handleDetails(event, val, journey)}
								control={<Switch checked={!!showDetails[journey.ShoppingLegSolutionId]} style={styles.toggleIcon} />}
								label={
									showDetails[journey.ShoppingLegSolutionId]
										? msgHideDetails
										: msgViewDetails
								}
							/>
						</div>

						{showDetails[journey.ShoppingLegSolutionId] && !isIntermediateTravelPointsLoading ? (
							<div>
								{journey.ShoppingFareTravelSegments.map((detail, i) => (
									<ItineraryDetails
										key={i}
										travelSegmentID={detail.TravelSegmentID}
										departStation={detail.ShoppingFareDepartStationSegment}
										arriveStation={detail.ShoppingFareArriveStationSegment}
										departTime={journey.ShoppingLegSolutionDepartTime}
										arriveTime={journey.ShoppingLegSolutionArriveTime}
										equipmentDesignator={`${detail.ShoppingFareMarketingServiceName} Train ${detail.TravelSegmentDesignator} ${detail.ShoppingFareEquipmentType}`}
										carrierLogo={detail.ShoppingFareMarketingCarrierLogo}
										connectionTimeText={detail.connectionTimeText}
										unconfirmedSchedule={detail.isScheduleConfirmed}
										warnings={detail.TravelSegmentWarnings}
										duration={journey.ShoppingLegSolutionDuration}
										passengerRights={detail.TravelSegmentPassengerRights}
										carbonOffset={detail.carbonOffset}
										intermediateTravelPoints={getIntermediateTravelPointByTravelSegmentId(detail.TravelSegmentID) ?? []}
									>
										{detail.carbonOffset && (
											<CarbonOffset value={detail.carbonOffset} />
										)}
									</ItineraryDetails>
								))}
							</div>
						) : (
							<>
								<TwoColRow
									firstColLabel={journey.ShoppingLegSolutionDepartStationName}
									secondColLabel={journey.ShoppingLegSolutionDepartTime}
								/>
								<TwoColRow
									firstColLabel={journey.ShoppingLegSolutionArriveStationName}
									secondColLabel={journey.ShoppingLegSolutionArriveTime}
								/>
							</>
						)}

						{journey.ShoppingLegSolutionMarketingCarriers &&
							journey.ShoppingLegSolutionMarketingCarriers[0] && (
								<img
									src={`${process.env.API_HOST}${process.env.BASE_PATH_SUFFIX}${journey.ShoppingLegSolutionMarketingCarrierLogoPath}`}
									alt={`${journey.ShoppingLegSolutionMarketingCarriers[0]} logo`}
									style={styles.logo}
								/>
							)}

						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={styles.selectButton}
							onClick={() =>
								handleJourneySelectClick(journey.ShoppingLegSolutionId)
							}
						>
							<FormattedMessage
								{...messages.lblAmendSeatReservationsDialogSelectThisJourney}
							/>
						</Button>
					</Paper>
				))}
			</div>
			<ReservationsDialog
				open={openSeatSelectorDialog}
				seatMapData={seatMapData}
				faresSelected={fromJS([selectedJourneyData])} // should choose 1
				seatReservation
				bikeReservations={[]}
				shoppingPassengers={fromJS(shoppingPassengers)}
				onChangeSeatReservations={handleSeatReservationsChange}
				onDiscardSeatReservations={() => {
					setOpenSeatSelectorDialog(false);
				}}
				selectedSeatReservations={selectedSeats}
				onChangeBikeReservations={() => null}
				onDiscardBikeReservations={() => null}
				selectedBikeReservations={{}}
			/>
			<PreviewDialog
				open={openPreviewDialog}
				handleDialogClose={() => setOpenPreviewDialog(false)}
				faresSelected={fromJS([selectedJourneyData])}
				ticketableFareGroups={ticketableFareGroups}
				selectedSeats={selectedSeats}
				confirmSeatsData={confirmSeatsData}
				refreshBookingParams={refreshBookingParams}
				closeAllDialogs={closeAllDialogs}
				isAmend={isAmend}
				legSolutionId={legSolutionId}
			/>
			<ExtendedSnackbar
				id="srtAmendSeatReservationsSnackBar"
				open={toastError !== ''}
				message={toastError}
				onClose={() => setToastError('')}
			/>
		</div>
	);
};

TrainJourneySelector.propTypes = {
	journeys: PropTypes.array,
	trainJourneysData: PropTypes.object,
	ticketableFareGroups: PropTypes.array,
	confirmSeatsData: PropTypes.object,
	refreshBookingParams: PropTypes.any,
	closeAllDialogs: PropTypes.func,
	isAmend: PropTypes.bool,
	legSolutionId: PropTypes.number,
};

export default TrainJourneySelector;
